import { useState } from "react";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import copy from "copy-to-clipboard";
import { getProvisionalUser } from "./service";
import "../../common/css/base.css";
import "../../common/css/dashboard.css";
import "../../common/css/feature-flag.css";

// NOTE: 検索前(null)と、検索したが該当なし(NOT_FOUND)を区別する。
const NOT_FOUND = "NOT_FOUND";

// NOTE: materialize-css(public/index.htmlでCDN読み込み)が素のinput/ulに
// NOTE: 下線やlist-style-type:noneを当ててくる。'browser-default'は
// NOTE: materialize自身が用意しているオプトアウト用クラス。
const NO_MATERIALIZE = "browser-default";

const Amp = (_) => {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || isLoading) {
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    setResult(null);
    setIsCopied(false);
    try {
      const data = await getProvisionalUser(trimmedEmail);
      setResult(data || NOT_FOUND);
    } catch (err) {
      console.error(err);
      setErrorMessage("情報の取得に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (!result.signUpUrl) {
      return;
    }
    copy(result.signUpUrl);
    setIsCopied(true);
  };

  // NOTE: ソフト期限切れは再送で復帰できるため、ハード期限切れと区別して出す。
  const renderExpiresTime = () => {
    if (!result.expiresTime) {
      return "-";
    }
    if (result.isHardExpired) {
      return (
        <>
          <span className="color-red text-bold">{result.expiresTime}</span>
          <br />
          <span className="color-red">
            期限切れ（招待から30日を超えているため再送不可）
          </span>
        </>
      );
    }
    if (result.isSoftExpired) {
      return (
        <>
          <span className="color-red text-bold">{result.expiresTime}</span>
          <br />
          <span className="color-red">期限切れ（再送可能）</span>
        </>
      );
    }
    return result.expiresTime;
  };

  const renderSignUpUrl = () => {
    if (!result.signUpUrl) {
      return (
        <span className="color-red">
          URLを組み立てられませんでした。サーバーの SIGN_UP_URL_TEMPLATE
          が設定されているか確認してください。
        </span>
      );
    }
    return (
      <>
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <a
            href={result.signUpUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              minWidth: 0,
              wordBreak: "break-all",
              lineHeight: 1.7,
            }}
          >
            {result.signUpUrl}
          </a>
          <Tooltip title="URLをコピー">
            <IconButton
              aria-label="URLをコピー"
              onClick={handleCopy}
              size="small"
              style={{ flexShrink: 0, marginLeft: "8px" }}
            >
              <FileCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        {isCopied && (
          <Typography variant="body2" style={{ marginTop: "4px" }}>
            コピーしました
          </Typography>
        )}
      </>
    );
  };

  const renderResult = () => {
    if (result === NOT_FOUND) {
      return (
        <Typography>
          該当する仮登録ユーザーが見つかりませんでした。メールアドレスをご確認ください。
        </Typography>
      );
    }
    return (
      <TableContainer className="dashboard-loguibld-table background-white">
        {/* NOTE: 下に伸びて注意書きやリンクが見切れないよう2列で並べる。 */}
        {/* NOTE: リンクだけは長いため全幅の1列にする。 */}
        <Table aria-label="table">
          <TableBody className="table-body">
            <ResultRow
              left={{ label: "工務店ID", value: result.builderId }}
              right={{ label: "工務店名", value: result.builderName || "-" }}
            />
            <ResultRow
              left={{ label: "名前", value: result.userName || "-" }}
              right={{ label: "ふりがな", value: result.userFurigana || "-" }}
            />
            <ResultRow
              left={{ label: "email", value: result.email }}
              right={{ label: "expires time", value: renderExpiresTime() }}
            />
            <ResultRow
              left={{
                label: "state",
                value: `${result.state}${
                  result.stateTxt ? `（${result.stateTxt}）` : ""
                }`,
              }}
              right={{ label: "token", value: result.token }}
            />
            <ResultRow
              left={{ label: "リンク", value: renderSignUpUrl() }}
              isFullWidth
            />
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderNotes = () => (
    <div
      style={{
        backgroundColor: "#FFF8E1",
        border: "1px solid #FFE082",
        borderRadius: "4px",
        padding: "16px",
      }}
    >
      <Grid container alignItems="center" style={{ marginBottom: "8px" }}>
        <ErrorOutlineIcon
          fontSize="small"
          style={{ color: "#F57F17", marginRight: "8px" }}
        />
        <Typography className="text-bold">注意</Typography>
      </Grid>
      <ul className={NO_MATERIALIZE} style={{ margin: 0, paddingLeft: "20px" }}>
        {[
          "expires timeが切れている場合は、URLをクリックするとメールが再送されます（ソフト期限）。その後、リロードしてURLを更新してください。",
          "登録済みの場合は、URLクリックでその旨が表示されます。",
          "期限切れでも招待から30日以内の場合は再送可能、30日を超えている場合は再送不可です。",
          "同じメールアドレスに複数の招待がある場合、最新のtokenのみ表示します。",
        ].map((note, index) => (
          <li
            key={index}
            style={{ lineHeight: 1.8, listStyleType: "disc", marginBottom: "6px" }}
          >
            <Typography variant="body2" component="span">
              {note}
            </Typography>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <Grid className="dashboard">
      <Typography variant="h5" className="dashboard-title text-bold">
        本登録URLの確認
      </Typography>

      {/* NOTE: 検索結果が伸びても注意書きが下へ押し出されないよう、右カラムに置く。 */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: "24px",
        }}
      >
        <div style={{ flex: "1 1 560px", minWidth: 0 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <TextField
                  autoComplete="off"
                  disabled={isLoading}
                  // NOTE: materializeの下線を打ち消すため実inputにクラスを当てる。
                  inputProps={{ className: NO_MATERIALIZE }}
                  label="メールアドレス"
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  style={{ minWidth: "360px" }}
                  type="email"
                  value={email}
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  disabled={!email.trim() || isLoading}
                  type="submit"
                  variant="contained"
                >
                  確認
                </Button>
              </Grid>
              {isLoading && (
                <Grid item>
                  <CircularProgress size={24} />
                </Grid>
              )}
            </Grid>
          </form>

          {errorMessage && (
            <Typography className="color-red" style={{ marginTop: "16px" }}>
              {errorMessage}
            </Typography>
          )}

          {result && (
            <Grid style={{ marginTop: "24px" }}>{renderResult()}</Grid>
          )}
        </div>

        {/* NOTE: AppBarがposition="static"のため、stickyでも被らない。 */}
        <aside
          style={{
            flex: "0 1 380px",
            minWidth: "300px",
            position: "sticky",
            top: "16px",
          }}
        >
          {renderNotes()}
        </aside>
      </div>
    </Grid>
  );
};

const LABEL_CELL_STYLE = {
  width: "120px",
  verticalAlign: "top",
  whiteSpace: "nowrap",
};

const ResultRow = ({ left, right, isFullWidth }) => (
  <TableRow>
    <TableCell className="text-bold" style={LABEL_CELL_STYLE}>
      {left.label}
    </TableCell>
    <TableCell
      colSpan={isFullWidth ? 3 : 1}
      style={{ verticalAlign: "top", wordBreak: "break-all" }}
    >
      {left.value}
    </TableCell>
    {!isFullWidth && (
      <>
        <TableCell className="text-bold" style={LABEL_CELL_STYLE}>
          {right.label}
        </TableCell>
        <TableCell style={{ verticalAlign: "top", wordBreak: "break-all" }}>
          {right.value}
        </TableCell>
      </>
    )}
  </TableRow>
);

export default Amp;
